<script lang="ts">
  import { Object3D, ObjectLoader, Vector3, WebGLRenderer } from 'three'
  import {
    GCODE_POLL_TIMEOUT_MS,
    useProjectGcodeQuery,
    useProjectQuery,
  } from '../../queries'
  import axios from 'axios'
  import { decompress } from '../../helpers/files'
  import { writable } from 'svelte/store'
  import { onMount, setContext } from 'svelte'
  import { usePageTitle } from '../../hooks/page-title'
  import i18n from '../../i18n'
  import { Canvas } from '@threlte/core'
  import Header from './components/Header.svelte'
  import Preview from '../../components/preview/Preview.svelte'
  import Button from '../../components/button/Button.svelte'
  import EmptyState from '../../components/empty-state/EmptyState.svelte'
  import {
    ChevronLeft,
    CircleAlert,
    LoaderCircle,
    RefreshCw,
  } from '@lucide/svelte'
  import { getPrinterColor, getPrinterId } from '../../helpers/printer'
  import GcodePreview from './components/GcodePreview.svelte'
  import Player from './components/Player.svelte'
  import Statistics from './components/Statistics.svelte'
  import { isPerspectiveCamera } from '../../stores/camera'
  import { fetchProjectGcode } from '../../api/projects'
  import { parse } from '../../helpers/scene-json'
  import { parseGcode } from '../../helpers/gcode'
  import { queryReactiveAccessor } from '../../helpers/tanstack-query'
  import { ThreeItemKeys } from '../../constants/three'
  import queryClient from '../../constants/query-client'

  let previewProgress = $state(0)
  const urlParams = new URLSearchParams(window.location.search)
  const printerId = urlParams.get('printer_id') as string
  Object3D.DEFAULT_UP = new Vector3(0, 0, 1)
  const { route } = $props()

  let shiftPressed = $state(false)
  let gcodeSize = $state(0)
  let parsedGcode = $state.raw<ParsedGcodeLine[]>([])
  // False while the gcode is being parsed into the preview, after it has been
  // fetched but before the parsed lines are ready ("building preview" phase).
  let previewBuilt = $state(false)
  let printer = $state<{
    name: string
    color: string
    model: PrinterModel
  }>()
  let displayCaptions = $state(false)
  let displayG0 = $state(true)

  /* svelte-ignore state_referenced_locally */
  const projectId = writable<string | undefined>(route.result.path.params.id)
  setContext('projectId', projectId)

  const getProjectQuery = useProjectQuery($projectId)

  const threeJsonUrl = $derived.by(
    queryReactiveAccessor(
      getProjectQuery,
      data => data?.data.compressedThreeJsonUrl,
    ),
  )

  let sceneStatus = $state<'loading' | 'success' | 'error'>('loading')

  const loadProjectScene = async (compressedThreeJsonUrl: string) => {
    sceneStatus = 'loading'
    try {
      const { data } = await axios.get(compressedThreeJsonUrl, {
        responseType: 'arraybuffer',
        decompress: true,
      })
      const stringifiedSceneJson = await decompress(data)

      const loader = new ObjectLoader()
      const parsedScene = loader.parse(parse(stringifiedSceneJson))

      parsedScene.traverse((child) => {
        if (getPrinterId(child) === printerId) {
          printer = {
            name: child.name,
            color: getPrinterColor(child),
            model: child.userData[ThreeItemKeys.PRINTER_MODEL] || 'maxiprinter',
          }
        }
      })
      sceneStatus = 'success'
    }
    catch (error) {
      console.error(error)
      sceneStatus = 'error'
    }
  }

  // Polls the backend every 2s until the asynchronous gcode generation is ready.
  const gcodeQuery = useProjectGcodeQuery($projectId, printerId)

  // Two-phase loading: while the query is not ready we show "generating gcode";
  // once the gcode is fetched we show "building preview" and defer the (potentially
  // heavy, main-thread-blocking) parse across two animation frames so that overlay
  // actually paints before the parse blocks.
  $effect(() => {
    const result = gcodeQuery.data
    if (result?.status !== 'ready') {
      previewBuilt = false
      return
    }

    previewBuilt = false
    let secondFrame = 0
    const firstFrame = requestAnimationFrame(() => {
      secondFrame = requestAnimationFrame(() => {
        parsedGcode = parseGcode(result.gcode)
        previewProgress = gcodeSize = parsedGcode.length
        previewBuilt = true
      })
    })

    return () => {
      cancelAnimationFrame(firstFrame)
      cancelAnimationFrame(secondFrame)
    }
  })

  // Flips true once we have waited GCODE_POLL_TIMEOUT_MS for the slice without it
  // resolving, so the UI can stop spinning and show an error instead of polling
  // forever. Armed on mount and re-armed by the Retry button (armPollTimeout).
  let pollTimedOut = $state(false)
  let pollTimeoutTimer: ReturnType<typeof setTimeout> | undefined

  const armPollTimeout = () => {
    clearTimeout(pollTimeoutTimer)
    pollTimedOut = false
    pollTimeoutTimer = setTimeout(() => {
      pollTimedOut = true
    }, GCODE_POLL_TIMEOUT_MS)
  }

  $effect(() => {
    if (!$projectId || !printerId) {
      return
    }
    armPollTimeout()
    return () => clearTimeout(pollTimeoutTimer)
  })

  const gcodeStatus = $derived(gcodeQuery.data?.status)
  const awaitingGcode = $derived(
    gcodeStatus !== 'ready' && gcodeStatus !== 'failed',
  )
  const timedOut = $derived(pollTimedOut && awaitingGcode)
  const isPreviewBuilding = $derived(gcodeStatus === 'ready' && !previewBuilt)
  const isPreviewFetching = $derived(
    sceneStatus === 'loading'
      || (awaitingGcode && !gcodeQuery.isError && !timedOut)
      || isPreviewBuilding,
  )
  const isPreviewError = $derived(
    !isPreviewFetching
      && (sceneStatus === 'error'
        || gcodeStatus === 'failed'
        || gcodeQuery.isError
        || timedOut),
  )
  const isPreviewReady = $derived(!isPreviewFetching && !isPreviewError)

  // A failed generation is cached server-side; a plain refetch would just hit the
  // same failed record and get a 422 again. Pinging with retry=true first flips it
  // back to pending and re-enqueues the slicing job, then resetting the query starts
  // a fresh polling window (clearing the poll-count backstop and the timeout timer).
  const retryPreview = async () => {
    armPollTimeout()
    if (sceneStatus === 'error' && threeJsonUrl) {
      loadProjectScene(threeJsonUrl)
    }
    if (gcodeStatus === 'failed' || gcodeQuery.isError || timedOut) {
      await fetchProjectGcode($projectId!, printerId, { retry: true })
      queryClient.resetQueries({
        queryKey: ['project', $projectId, 'gcode', printerId],
      })
    }
  }

  $effect(() => {
    usePageTitle(
      $i18n.t('projectPreview.metaTitle', {
        projectName: getProjectQuery.data?.data.name,
        printerName: printer?.name,
      }),
    )
  })

  $effect(() => {
    if (threeJsonUrl) {
      loadProjectScene(threeJsonUrl)
    }
  })

  onMount(() => {
    $isPerspectiveCamera = true

    // The gcode cache is keyed only on (projectId, printerId) with staleTime
    // Infinity, so re-entering the preview after editing the scene would reuse the
    // stale entry. The backend keys its own cache on the scene signature, so reset
    // the query on mount to force a fresh fetch: unchanged scenes hit the server
    // cache cheaply, changed scenes get the new gcode without a full page reload.
    queryClient.resetQueries({
      queryKey: ['project', $projectId, 'gcode', printerId],
    })

    const onKeyUp = (e: KeyboardEvent) => {
      if (e.key === 'Shift') {
        shiftPressed = false
      }
    }

    const onKeyDown = (e: KeyboardEvent) => {
      if (e.target && 'tagName' in e.target && e.target.tagName === 'INPUT') {
        return
      }

      e.preventDefault()
      switch (e.key) {
        case 'ArrowRight':
          previewProgress = Math.min(previewProgress + 1, gcodeSize)
          break
        case 'ArrowLeft':
          previewProgress = Math.max(previewProgress - 1, 0)
          break
        case 'ArrowUp':
          previewProgress = Math.min(
            previewProgress + (shiftPressed ? 100 : 10),
            gcodeSize,
          )
          break
        case 'ArrowDown':
          previewProgress = Math.max(
            previewProgress - (shiftPressed ? 100 : 10),
            0,
          )
          break
        case 'Shift':
          shiftPressed = true
          break
      }
    }

    window.addEventListener('keydown', onKeyDown)
    window.addEventListener('keyup', onKeyUp)

    return () => {
      window.removeEventListener('keydown', onKeyDown)
      window.removeEventListener('keyup', onKeyUp)
    }
  })
</script>

<div class="container">
  <Header {printerId} />
  <div class="canvas">
    <div class="back-button">
      <Button href={`/projects/${$projectId}`} variant="secondary">
        <ChevronLeft />
        {$i18n.t('projectPreview.backToEditor')}
      </Button>
    </div>
    {#if isPreviewError}
      <div class="state-container">
        <EmptyState
          icon={CircleAlert}
          title={$i18n.t('projectPreview.errorState.title')}
          subtitle={$i18n.t('projectPreview.errorState.subtitle')}
          buttonIcon={RefreshCw}
          buttonLabel={$i18n.t('projectPreview.errorState.retryButton')}
          onButtonClick={retryPreview}
        />
      </div>
    {:else if isPreviewFetching}
      <div class="state-container">
        <EmptyState
          icon={LoaderCircle}
          spin
          subtitle={$i18n.t(
            isPreviewBuilding
              ? 'projectPreview.loadingState.buildingPreview'
              : 'projectPreview.loadingState.loadingGcode',
          )}
        />
      </div>
    {:else}
      <Statistics {parsedGcode} progress={previewProgress} />
      <GcodePreview
        hidden={!displayCaptions}
        {parsedGcode}
        progress={previewProgress}
      />
      <Canvas
        createRenderer={(canvas) => {
          // Allow the browser to restore the WebGL context instead of tearing the
          // whole canvas down if the GPU ever drops it.
          canvas.addEventListener('webglcontextlost', event =>
            event.preventDefault(),
          )
          return new WebGLRenderer({ canvas, stencil: true })
        }}
      >
        <Preview
          {displayG0}
          {parsedGcode}
          {printer}
          progress={previewProgress}
        />
      </Canvas>
    {/if}
  </div>
  {#if isPreviewReady}
    <Player
      {gcodeSize}
      {printer}
      bind:previewProgress
      bind:displayCaptions
      bind:displayG0
    />
  {/if}
</div>

<style lang="scss">
  .container {
    align-items: stretch;
    display: flex;
    flex-direction: column;
    height: 100vh;
    overflow: hidden;
    width: 100vw;

    .canvas {
      display: flex;
      flex: 1 1 auto;
      min-width: 0;
      min-height: 0;
      position: relative;

      .back-button {
        position: absolute;
        top: 1rem;
        left: 1rem;
        z-index: 5;
      }

      .state-container {
        align-items: center;
        display: flex;
        flex: 1 1 auto;
        justify-content: center;
      }
    }
  }
</style>
